import type { HistoryResponse } from '@rfleet/shared';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Download, Pause, Play, SkipBack } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { EmptyState, Field, Skeleton } from '../../components/ui';
import { api, type Paginated } from '../../lib/api';
import { fmtDateTime, fmtDuration, fmtNumber } from '../../lib/format';
import { MapView } from '../../map/MapView';
import { RouteLayer } from '../../map/RouteLayer';
import type { VehicleRow } from '../vehicles/VehiclesPage';

const toLocalInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);

export function HistoryPage() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [vehicleId, setVehicleId] = useState(params.get('vehicle') ?? '');
  const now = new Date();
  const [from, setFrom] = useState(toLocalInput(new Date(now.getTime() - 24 * 3600_000)));
  const [to, setTo] = useState(toLocalInput(now));
  const [applied, setApplied] = useState<{ vehicleId: string; from: string; to: string } | null>(vehicleId ? { vehicleId, from: new Date(from).toISOString(), to: new Date(to).toISOString() } : null);
  const [playing, setPlaying] = useState(false);
  const [idx, setIdx] = useState(0);
  const [speed, setSpeed] = useState(10);
  const timer = useRef<number | null>(null);

  const vehicles = useQuery({ queryKey: ['vehicles-lite'], queryFn: () => api<Paginated<VehicleRow>>('/vehicles?pageSize=200').then((r) => r.data) });
  const history = useQuery({
    queryKey: ['history', applied],
    enabled: !!applied,
    queryFn: () => api<HistoryResponse>(`/history/vehicles/${applied!.vehicleId}?from=${encodeURIComponent(applied!.from)}&to=${encodeURIComponent(applied!.to)}`),
  });
  const points = history.data?.points ?? [];
  const stops = useMemo(() => (history.data?.stops ?? []).map((s) => ({ id: s.id, lat: s.lat, lon: s.lon, kind: s.kind, label: `${s.kind === 'IDLE' ? t('status.IDLE') : t('status.STOPPED')} ${fmtDuration(s.durationSec, i18n.language)}` })), [history.data, t, i18n.language]);

  useEffect(() => {
    setIdx(0);
    setPlaying(false);
  }, [history.data]);
  useEffect(() => {
    if (!playing) return;
    timer.current = window.setInterval(() => setIdx((i) => (i + 1 >= points.length ? (setPlaying(false), i) : i + 1)), 1000 / speed);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [playing, speed, points.length]);

  const apply = () => {
    if (!vehicleId) return;
    setApplied({ vehicleId, from: new Date(from).toISOString(), to: new Date(to).toISOString() });
    setParams({ vehicle: vehicleId }, { replace: true });
  };
  const exportCsv = () => {
    const rows = [['time', 'lat', 'lon', 'speed_kph', 'heading', 'ignition', 'source'], ...points.map((p) => [p.at, p.lat, p.lon, p.speedKph ?? '', p.heading ?? '', p.ignition ?? '', p.source])];
    const blob = new Blob([rows.map((r) => r.join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `history-${applied?.vehicleId}-${applied?.from.slice(0, 10)}.csv`;
    a.click();
  };
  const cur = points[idx];

  return (
    <div className="flex flex-col gap-3">
      <div className="card flex flex-wrap items-end gap-3 p-3">
        <div className="min-w-48 flex-1"><Field label={t('vehicles.vehicle')}>{(id) => <select id={id} className="input" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}><option value="">—</option>{vehicles.data?.map((v) => <option key={v.id} value={v.id}>{v.plateAr}{v.name ? ` · ${v.name}` : ''}</option>)}</select>}</Field></div>
        <Field label={t('history.from')}>{(id) => <input id={id} type="datetime-local" dir="ltr" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />}</Field>
        <Field label={t('history.to')}>{(id) => <input id={id} type="datetime-local" dir="ltr" className="input" value={to} onChange={(e) => setTo(e.target.value)} />}</Field>
        <button className="btn-primary" onClick={apply} disabled={!vehicleId}>{t('history.show')}</button>
        {points.length ? <button className="btn-secondary" onClick={exportCsv}><Download size={16} /> CSV</button> : null}
      </div>

      {history.data ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Stat label={t('history.distance')} value={`${fmtNumber(history.data.summary.distanceKm, i18n.language, 1)} ${t('units.km')}`} />
          <Stat label={t('history.moving')} value={fmtDuration(history.data.summary.movingSec, i18n.language)} />
          <Stat label={t('status.STOPPED')} value={fmtDuration(history.data.summary.stoppedSec, i18n.language)} />
          <Stat label={t('status.IDLE')} value={fmtDuration(history.data.summary.idleSec, i18n.language)} />
          <Stat label={t('history.maxSpeed')} value={`${history.data.summary.maxSpeedKph} ${t('units.kph')}`} />
        </div>
      ) : null}

      <div className="flex h-[calc(100dvh-380px)] min-h-[420px] flex-col gap-3 lg:flex-row">
        <div className="card relative min-h-[320px] flex-1 overflow-hidden">
          <MapView zoom={6}>
            <RouteLayer points={points} stops={stops} playhead={cur ?? null} fit={!playing} />
          </MapView>
          {points.length ? (
            <div className="absolute inset-x-3 bottom-3 flex items-center gap-2 rounded-lg border border-border bg-surface/95 px-3 py-2 shadow-overlay">
              <button className="btn-ghost size-8 !p-0" aria-label={t('history.restart')} onClick={() => { setIdx(0); setPlaying(false); }}><SkipBack size={16} /></button>
              <button className="btn-primary size-8 !p-0" aria-label={playing ? t('history.pause') : t('history.play')} onClick={() => setPlaying((p) => !p)}>{playing ? <Pause size={16} /> : <Play size={16} />}</button>
              <input type="range" min={0} max={Math.max(0, points.length - 1)} value={idx} onChange={(e) => setIdx(Number(e.target.value))} className="flex-1 accent-brand" aria-label={t('history.position')} dir="ltr" />
              <select className="input !w-auto !min-h-8 !py-1 text-xs" value={speed} onChange={(e) => setSpeed(Number(e.target.value))} aria-label={t('history.speed')}>{[1, 5, 10, 30, 60].map((s) => <option key={s} value={s}>{s}×</option>)}</select>
              <span className="w-40 text-end text-xs text-text-muted" dir="ltr">{cur ? `${fmtDateTime(cur.at, i18n.language)} · ${Math.round(cur.speedKph ?? 0)} ${t('units.kph')}` : ''}</span>
            </div>
          ) : null}
        </div>
        <aside className="card flex w-full flex-col overflow-hidden lg:w-[320px] lg:flex-none">
          <div className="border-b border-border-soft px-3 py-2 text-sm font-bold">{t('history.stops')} {history.data ? `(${history.data.stops.length})` : ''}</div>
          <div className="flex-1 overflow-y-auto">
            {history.isLoading ? <div className="p-3"><Skeleton className="h-40" /></div> : !applied ? <EmptyState title={t('history.pickVehicle')} /> : !history.data?.stops.length ? <EmptyState title={t('common.empty')} /> : history.data.stops.map((s) => (
              <div key={s.id} className="flex items-start gap-2 border-b border-border-soft px-3 py-2 text-xs">
                <span className={clsx('mt-1 size-2.5 rounded-full', s.kind === 'IDLE' ? 'bg-warning' : 'bg-neutral')} />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{s.kind === 'IDLE' ? t('status.IDLE') : t('status.STOPPED')} · {fmtDuration(s.durationSec, i18n.language)}</div>
                  <div className="text-text-muted">{fmtDateTime(s.startedAt, i18n.language)}</div>
                  <div className="truncate text-text-muted">{s.placeName ?? s.address ?? `${s.lat.toFixed(5)}, ${s.lon.toFixed(5)}`}</div>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return <div className="card p-3"><div className="text-xs text-text-muted">{label}</div><div className="mt-0.5 text-base font-bold">{value}</div></div>;
}
